/**
 * TemplateReviewStep.tsx
 * Enterprise template editor canvas (opened from Preview).
 *
 * Layout (top → bottom):
 *  1. Sticky action bar — close / page status / zoom / edit / save / download / primary CTA
 *  2. Main grid — canvas (3) + Excel field palette (1)
 *  3. Page tabs with per-page Duplicate/Delete only (no duplicated toolbar buttons)
 *  4. Optional condition bar + format toolbar (edit mode only)
 *  5. Word canvas
 */
import * as React from "react";
import type { DocxMarkerField } from "@/utils/docxMarker";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  RotateCcw,
  CheckCircle2,
  Search,
  Copy,
  Check,
  ZoomIn,
  ZoomOut,
  ArrowLeft,
  ArrowRight,
  Trash2,
  Download,
  Pencil,
  X,
  PanelRightClose,
  PanelRightOpen,
  ChevronRight,
  Scissors,
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { TemplateFormatToolbar } from "./TemplateFormatToolbar";
import { cn } from "@/lib/utils";
import { toastInfo, toastSuccess } from "@/utils/toast";
import { toTemplateVar, templateVarExpr } from "@/utils/templateVar";
import type { FieldFormattingConfig } from "@/utils/formatter";
import {
  applyTemplateOverlaysToContainer,
  captureTemplateOverlays,
  removeOverlay,
  updateOverlay,
  type TemplateOverlay,
} from "@/utils/templateOverlays";
import { useI18n } from "@/i18n";

/** Sentinel: split by row count (matches SPLIT_FIELD_BY_ROWS in the wizard). */
const SPLIT_BY_ROWS = "__rows__";

export interface DocxFileState {
  fileName: string;
  fileData: ArrayBuffer | Uint8Array;
  fields: DocxMarkerField[];
  errors: string[];
  warnings: string[];
}

/** A single undo/redo checkpoint: canvas DOM snapshot + overlays at that moment. */
interface UndoEntry {
  html: string;
  overlays: TemplateOverlay[];
}

interface TemplateReviewStepProps {
  docxFile: DocxFileState | null;
  headers: string[];
  primarySheetName: string;
  secondarySheetName?: string;
  joinedAddedColumns?: string[];
  templatePreviewRef: React.RefObject<HTMLDivElement | null>;
  busy: boolean;
  templateEditMode: boolean;
  onRenderTemplatePreview: () => Promise<void>;
  onToggleTemplateEdit: () => void;
  onSaveTemplateEdits: () => void | boolean | Promise<void | boolean>;
  /** Download the edited template DOCX file. */
  onDownloadTemplate?: () => void;
  /** Duplicate the page (Word section) at the given index. Returns copy index or false. */
    onDuplicateSection?: (pageIndex: number) => void | boolean | number | Promise<void | boolean | number>;
    /** Delete the page (Word section) at the given index. Returns next index or false. */
    onDeleteSection?: (pageIndex: number) => void | boolean | number | Promise<void | boolean | number>;
  onProceedToPreview: () => void | Promise<void>;
  /** When omitted, the back button is hidden (review is embedded in upload). */
  onBackToUpload?: () => void;
  /** Disable "chốt & sang xem trước" until Excel + template are ready. */
  canProceed?: boolean;
  backLabel?: string;
  proceedLabel?: string;
  /** Condition Page bar & editor for setting display rules on the template page. */
  conditionBar?: React.ReactNode;
  /** Callback when active page changes in review step. */
  onActivePageChange?: (pageIndex: number) => void;
  /** Page (Word section) to open on mount — jump straight to the frame the user clicked Edit on. */
  initialActivePage?: number;
  /** Carton-split column ("" = split by row count). */
  splitField?: string;
  /** Change carton-split column for the current page. */
  onSplitFieldChange?: (field: string) => void;
  /** Only numeric columns — the ones usable as a split count. */
  numericColumns?: string[];
  /** Free-form text/image overlays added on top of the template pages. */
  overlays?: TemplateOverlay[];
  /** Called whenever overlays are added/moved/removed. */
  onOverlaysChange?: (overlays: TemplateOverlay[]) => void;
  /** Field formatting config (number/currency/percent/date) keyed by field name. */
  formats?: Record<string, FieldFormattingConfig>;
  /** Called when a field's display format changes from the editor toolbar. */
  onFormatsChange?: (formats: Record<string, FieldFormattingConfig>) => void;
  /** Restore a saved canvas snapshot (undo/redo) and re-apply edit mode. */
  onRestoreCanvasSnapshot?: (html: string) => void;
}

export function TemplateReviewStep({
  docxFile,
  headers,
  primarySheetName,
  secondarySheetName,
  joinedAddedColumns = [],
  templatePreviewRef,
  busy,
  templateEditMode,
  onRenderTemplatePreview,
  onToggleTemplateEdit,
  onSaveTemplateEdits,
  onDownloadTemplate,
  onDuplicateSection,
  onDeleteSection,
  onProceedToPreview,
  onBackToUpload,
  canProceed = true,
  backLabel,
  proceedLabel,
  conditionBar,
  onActivePageChange,
  initialActivePage,
  splitField,
  onSplitFieldChange,
  numericColumns,
  overlays = [],
  onOverlaysChange,
  formats = {},
  onFormatsChange,
  onRestoreCanvasSnapshot,
}: TemplateReviewStepProps) {
  const { t } = useI18n();
  const [fieldSearch, setFieldSearch] = React.useState("");
  const [copiedField, setCopiedField] = React.useState<string | null>(null);
  const [zoom, setZoom] = React.useState(1);
  const [paletteOpen, setPaletteOpen] = React.useState(true);
const [excelColOpen, setExcelColOpen] = React.useState(true);
  /** Carton-split ("Tách trang") config card collapsed state. */
  const [splitOpen, setSplitOpen] = React.useState(true);
  /** Currently selected overlay id (for delete / style editing). */
  const [selectedOverlayId, setSelectedOverlayId] = React.useState<string | null>(null);
  /** Undo/redo stacks for the template canvas (snapshot DOM, capped). */
  const [undoStack, setUndoStack] = React.useState<UndoEntry[]>([]);
  const [redoStack, setRedoStack] = React.useState<UndoEntry[]>([]);
  const MAX_HISTORY = 50;
  /** Latest overlays kept in a ref so undo/redo capture the current value. */
  const overlaysRef = React.useRef(overlays);
  overlaysRef.current = overlays;
  /** Autocomplete popup for `{{…` template syntax. */
  const [ac, setAc] = React.useState<{
    query: string;
    x: number;
    y: number;
    node: Node;
    offset: number;
  } | null>(null);
  const acIndexRef = React.useRef(0);
  const [dragOverCanvas, setDragOverCanvas] = React.useState(false);
  /** Physical Word pages (`section.docx`) — one visible at a time. */
  const [pageCount, setPageCount] = React.useState(0);
  const [activePage, setActivePage] = React.useState(() =>
    initialActivePage && initialActivePage > 0 ? initialActivePage : 0
  );

  React.useEffect(() => {
    onActivePageChange?.(activePage);
  }, [activePage, onActivePageChange]);

  /**
   * Record the current canvas DOM + overlays as an undo checkpoint (called
   * after an edit commits). Undo restores the previous checkpoint; a fresh
   * edit clears redo.
   */
  const recordEdit = React.useCallback(() => {
    const container = templatePreviewRef.current;
    if (!container) return;
    const entry: UndoEntry = { html: container.innerHTML, overlays: overlaysRef.current };
    setUndoStack((prev) => [...prev.slice(-(MAX_HISTORY - 1)), entry]);
    setRedoStack([]);
  }, [templatePreviewRef]);

  /** Restore a snapshot (DOM + overlays) and re-apply edit mode. */
  const restoreSnapshot = React.useCallback(
    (entry: UndoEntry) => {
      onRestoreCanvasSnapshot?.(entry.html);
      if (onOverlaysChange) onOverlaysChange(entry.overlays);
      setSelectedOverlayId(null);
    },
    [onRestoreCanvasSnapshot, onOverlaysChange]
  );

  const handleUndo = React.useCallback(() => {
    const container = templatePreviewRef.current;
    if (!container || undoStack.length < 2) return;
    // undoStack[last] is the current state; undoStack[last-1] is the target.
    const current = undoStack[undoStack.length - 1];
    const target = undoStack[undoStack.length - 2];
    setUndoStack(undoStack.slice(0, -1));
    setRedoStack((r) => [...r, current]);
    restoreSnapshot(target);
  }, [undoStack, templatePreviewRef, restoreSnapshot]);

  const handleRedo = React.useCallback(() => {
    const container = templatePreviewRef.current;
    if (!container || redoStack.length === 0) return;
    const next = redoStack[redoStack.length - 1];
    setRedoStack(redoStack.slice(0, -1));
    setUndoStack((u) => [...u, { html: container.innerHTML, overlays: overlaysRef.current }]);
    restoreSnapshot(next);
  }, [redoStack, templatePreviewRef, restoreSnapshot]);

  // Re-apply overlays onto the rendered canvas whenever they change or the
  // preview re-renders. The canvas is re-rendered from the DOCX after every
  // save, which detaches overlay nodes, so this keeps them in sync.
  React.useEffect(() => {
    const container = templatePreviewRef.current;
    if (!container) return;
    applyTemplateOverlaysToContainer(container, overlays);
  }, [overlays, templatePreviewRef, templateEditMode]);

  // Seed the undo stack with the initial canvas state the first time edit mode
  // is enabled, so the first Undo can return to the untouched template.
  const seededHistoryRef = React.useRef(false);
  React.useEffect(() => {
    if (!templateEditMode || seededHistoryRef.current) return;
    const container = templatePreviewRef.current;
    if (!container || !container.innerHTML) return;
    seededHistoryRef.current = true;
    setUndoStack([{ html: container.innerHTML, overlays }]);
    setRedoStack([]);
  }, [templateEditMode, templatePreviewRef, overlays]);

  // In edit mode, make overlay nodes draggable (pointer-events:auto) and update
  // their % position live; capture the new positions back into state on drop.
  React.useEffect(() => {
    const container = templatePreviewRef.current;
    if (!container || !templateEditMode) return;
    const nodes = Array.from(
      container.querySelectorAll<HTMLElement>("[data-overlay-id]")
    );
    if (nodes.length === 0) return;

    const section = container.querySelector("section.docx") as HTMLElement | null;
    if (!section) return;

    const cleanups: Array<() => void> = [];
    nodes.forEach((node) => {
      node.style.pointerEvents = "auto";
      node.style.cursor = "move";
      node.classList.add("ring-1", "ring-blue-400/70", "hover:ring-2");

      let dragging = false;
      let startX = 0;
      let startY = 0;
      let startLeft = 0;
      let startTop = 0;

      const onDown = (e: PointerEvent) => {
        e.preventDefault();
        e.stopPropagation();
        const id = node.getAttribute("data-overlay-id");
        setSelectedOverlayId(id);
        dragging = true;
        startX = e.clientX;
        startY = e.clientY;
        startLeft = parseFloat(node.style.left) || 0;
        startTop = parseFloat(node.style.top) || 0;
        node.classList.add("ring-2", "ring-blue-500", "shadow-md");
      };
      const onMove = (e: PointerEvent) => {
        if (!dragging) return;
        const sw = section.offsetWidth || 1;
        const sh = section.offsetHeight || 1;
        const dxPct = ((e.clientX - startX) / sw) * 100;
        const dyPct = ((e.clientY - startY) / sh) * 100;
        node.style.left = `${Math.max(0, Math.min(100, startLeft + dxPct))}%`;
        node.style.top = `${Math.max(0, Math.min(100, startTop + dyPct))}%`;
      };
      const onUp = () => {
        if (!dragging) return;
        dragging = false;
        node.classList.remove("ring-2", "ring-blue-500", "shadow-md");
        onOverlaysChange?.(captureTemplateOverlays(container));
      };

      node.addEventListener("pointerdown", onDown);
      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", onUp);
      cleanups.push(() => {
        node.removeEventListener("pointerdown", onDown);
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onUp);
      });
    });

    // Resize handle for image overlays: a corner grip that changes width %.
    const imgNodes = Array.from(
      container.querySelectorAll<HTMLElement>('[data-overlay-kind="image"]')
    );
    imgNodes.forEach((node) => {
      const grip = document.createElement("div");
      grip.className = "overlay-resize-grip";
      grip.style.cssText =
        "position:absolute;right:-6px;bottom:-6px;width:12px;height:12px;" +
        "background:#3b82f6;border:2px solid #fff;border-radius:2px;cursor:se-resize;" +
        "z-index:20;display:none;";
      node.appendChild(grip);

      let resizing = false;
      let startX = 0;
      let startW = 0;

      const onGripDown = (e: PointerEvent) => {
        e.preventDefault();
        e.stopPropagation();
        resizing = true;
        startX = e.clientX;
        startW = parseFloat(node.style.width) || 20;
      };
      const onGripMove = (e: PointerEvent) => {
        if (!resizing) return;
        const sw = section.offsetWidth || 1;
        const dxPct = ((e.clientX - startX) / sw) * 100;
        node.style.width = `${Math.max(5, Math.min(100, startW + dxPct))}%`;
      };
      const onGripUp = () => {
        if (!resizing) return;
        resizing = false;
        onOverlaysChange?.(captureTemplateOverlays(container));
      };

      grip.addEventListener("pointerdown", onGripDown);
      window.addEventListener("pointermove", onGripMove);
      window.addEventListener("pointerup", onGripUp);
      cleanups.push(() => {
        grip.removeEventListener("pointerdown", onGripDown);
        window.removeEventListener("pointermove", onGripMove);
        window.removeEventListener("pointerup", onGripUp);
        grip.remove();
      });
    });

    // Show the resize grip only on the selected image overlay.
    const syncGrips = () => {
      imgNodes.forEach((node) => {
        const grip = node.querySelector(".overlay-resize-grip") as HTMLElement | null;
        if (!grip) return;
        const id = node.getAttribute("data-overlay-id");
        grip.style.display = id === selectedOverlayId ? "block" : "none";
      });
    };
    syncGrips();
    cleanups.push(() => {
      imgNodes.forEach((node) => {
        const grip = node.querySelector(".overlay-resize-grip") as HTMLElement | null;
        if (grip) grip.style.display = "none";
      });
    });

    return () => {
      cleanups.forEach((fn) => fn());
      nodes.forEach((node) => {
        node.style.pointerEvents = "none";
        node.style.cursor = "";
        node.classList.remove("ring-1", "ring-blue-400/70", "hover:ring-2", "ring-2", "ring-blue-500", "shadow-md");
      });
    };
  }, [templateEditMode, overlays, templatePreviewRef, onOverlaysChange, selectedOverlayId]);

  /** Add a new free-form text line overlay to the active page. */
  const handleAddTextOverlay = React.useCallback(() => {
    const container = templatePreviewRef.current;
    if (!container) return;
    const section = container.querySelector("section.docx") as HTMLElement | null;
    if (!section) return;
    const id = `ov-text-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
    const next: TemplateOverlay[] = [
      ...overlays,
      {
        id,
        kind: "text",
        x: 10,
        y: 10,
        width: 60,
        text: t("editor.newText"),
        fontSize: 14,
        fontWeight: "normal",
        color: "#000000",
        textAlign: "left",
        zIndex: 10,
      },
    ];
    onOverlaysChange?.(next);
    overlaysRef.current = next;
    recordEdit();
    toastInfo(t("editor.addedText"), t("editor.addedTextDesc"));
  }, [overlays, onOverlaysChange, templatePreviewRef, recordEdit]);

  /** Add a new image overlay (base64) to the active page. */
  const handleAddImageOverlay = React.useCallback(() => {
    const container = templatePreviewRef.current;
    if (!container) return;
    const section = container.querySelector("section.docx") as HTMLElement | null;
    if (!section) return;
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.onchange = () => {
      const file = input.files?.[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = () => {
        const dataUrl = String(reader.result ?? "");
        const id = `ov-img-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
        const next: TemplateOverlay[] = [
          ...overlays,
          { id, kind: "image", x: 10, y: 10, width: 20, dataUrl, zIndex: 10 },
        ];
        onOverlaysChange?.(next);
        overlaysRef.current = next;
        recordEdit();
        toastSuccess(t("editor.insertedImage"), t("editor.insertedImageDesc"));
      };
      reader.readAsDataURL(file);
    };
    input.click();
  }, [overlays, onOverlaysChange, templatePreviewRef, recordEdit]);

  /** Remove the selected overlay. */
  const handleDeleteOverlay = React.useCallback(() => {
    if (!selectedOverlayId) return;
    const next = removeOverlay(overlays, selectedOverlayId);
    onOverlaysChange?.(next);
    overlaysRef.current = next;
    recordEdit();
    setSelectedOverlayId(null);
    toastInfo(t("editor.deleted"), t("editor.deletedDesc"));
  }, [selectedOverlayId, overlays, onOverlaysChange, recordEdit]);

  /** Patch the selected overlay (style / text / width). */
  const handlePatchOverlay = React.useCallback(
    (patch: Partial<TemplateOverlay>) => {
      if (!selectedOverlayId) return;
      const next = updateOverlay(overlays, selectedOverlayId, patch);
      onOverlaysChange?.(next);
      overlaysRef.current = next;
      recordEdit();
    },
    [selectedOverlayId, overlays, onOverlaysChange, recordEdit]
  );

  const selectedOverlay = React.useMemo(
    () => overlays.find((o) => o.id === selectedOverlayId) ?? null,
    [overlays, selectedOverlayId]
  );

  // ── Autocomplete for `{{…` template syntax ────────────────────────────
  const acOptions = React.useMemo(() => {
    if (!ac) return [];
    const q = ac.query.toLowerCase();
    return headers.filter((h) => h.toLowerCase().includes(q)).slice(0, 20);
  }, [ac, headers]);

  // Reset keyboard index whenever the option list changes
  React.useEffect(() => {
    acIndexRef.current = 0;
  }, [acOptions.length, ac?.query]);

  const closeAc = React.useCallback(() => setAc(null), []);

  const insertField = React.useCallback(
    (fieldName: string) => {
      const sel = window.getSelection();
      let node: Node | null = null;
      let offset = 0;
      if (ac) {
        node = ac.node;
        offset = ac.offset;
      } else if (sel && sel.rangeCount > 0) {
        const r = sel.getRangeAt(0);
        node = r.startContainer;
        offset = r.startOffset;
      }
      if (!node) return;
      try {
        const range = document.createRange();
        range.setStart(node, offset);
        range.collapse(true);
        const text = document.createTextNode(`{{${fieldName}}}`);
        range.insertNode(text);
        // Move caret after the inserted text
        const after = document.createRange();
        after.setStartAfter(text);
        after.collapse(true);
        sel?.removeAllRanges();
        sel?.addRange(after);
        // Mark paragraph as edited so save picks it up
        const p = (node.parentElement as HTMLElement | null)?.closest(
          "p, td, th, h1, h2, h3, h4"
        ) as HTMLElement | null;
        p?.setAttribute("data-format-edited", "true");
        p?.focus();
      } catch {
        // insertion failed — ignore
      }
    },
    [ac]
  );

  const insertAcField = React.useCallback(
    (fieldName: string) => {
      insertField(fieldName);
      closeAc();
    },
    [insertField, closeAc]
  );

  // Watch keystrokes inside the editable canvas
  React.useEffect(() => {
    const container = templatePreviewRef.current;
    if (!container) return;

    const onKeyUp = (e: KeyboardEvent) => {
      if (!templateEditMode) return;
      if (e.key === "Escape") {
        closeAc();
        return;
      }
      // Arrow navigation through the popup
      if (ac && (e.key === "ArrowDown" || e.key === "ArrowUp")) {
        e.preventDefault();
        const len = acOptions.length;
        if (len > 0) {
          acIndexRef.current =
            e.key === "ArrowDown"
              ? (acIndexRef.current + 1) % len
              : (acIndexRef.current - 1 + len) % len;
        }
        return;
      }
      if (ac && e.key === "Enter") {
        const item = acOptions[acIndexRef.current];
        if (item) {
          e.preventDefault();
          insertAcField(item);
        }
        return;
      }

      const sel = window.getSelection();
      if (!sel || sel.rangeCount === 0) return;
      const range = sel.getRangeAt(0);
      const node = range.startContainer;
      if (node.nodeType !== Node.TEXT_NODE) {
        closeAc();
        return;
      }
      const text = (node.textContent ?? "").slice(0, range.startOffset);
      // Last unclosed `{{` before caret
      const openIdx = text.lastIndexOf("{{");
      if (openIdx === -1 || text.slice(openIdx + 2).includes("}}")) {
        closeAc();
        return;
      }
      const query = text.slice(openIdx + 2);
      if (query.length > 40) {
        closeAc();
        return;
      }
      // Position popup at caret
      const caretRect = range.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();
      setAc({
        query,
        x: caretRect.left - containerRect.left,
        y: caretRect.bottom - containerRect.top + 4,
        node,
        offset: range.startOffset,
      });
    };

    const onKeyDown = (e: KeyboardEvent) => {
      if (ac && (e.key === "ArrowDown" || e.key === "ArrowUp" || e.key === "Enter")) {
        e.preventDefault();
      }
    };

    container.addEventListener("keyup", onKeyUp);
    container.addEventListener("keydown", onKeyDown);
    container.addEventListener("blur", closeAc, true);
    return () => {
      container.removeEventListener("keyup", onKeyUp);
      container.removeEventListener("keydown", onKeyDown);
      container.removeEventListener("blur", closeAc, true);
    };
  }, [templatePreviewRef, templateEditMode, ac, acOptions, insertAcField, closeAc]);

  const templateFieldNames = React.useMemo(() => {
    if (!docxFile) return new Set<string>();
    return new Set(docxFile.fields.map((f) => f.fieldName.trim().toLowerCase()));
  }, [docxFile]);

  const filteredHeaders = React.useMemo(() => {
    if (!fieldSearch.trim()) return headers;
    const q = fieldSearch.toLowerCase();
    return headers.filter((h) => h.toLowerCase().includes(q));
  }, [headers, fieldSearch]);

  const matchedCount = React.useMemo(
    () => headers.filter((h) => templateFieldNames.has(h.trim().toLowerCase())).length,
    [headers, templateFieldNames]
  );

  const handleCopyField = (headerName: string) => {
    const textToCopy = templateVarExpr(headerName) || `{{${headerName}}}`;
    navigator.clipboard.writeText(textToCopy).then(() => {
      setCopiedField(headerName);
      toastSuccess(`Đã chép ${textToCopy}`, t("editor.pasteHint"));
      setTimeout(() => setCopiedField(null), 2000);
    });
  };

  const handleZoomIn = () => setZoom((prev) => Math.min(prev + 0.1, 1.5));
  const handleZoomOut = () => setZoom((prev) => Math.max(prev - 0.1, 0.6));
  const handleResetZoom = () => setZoom(1);

  const syncPageCount = React.useCallback(() => {
    const container = templatePreviewRef.current;
    if (!container) return;
    const n = container.querySelectorAll("section.docx").length;
    setPageCount(n);
    setActivePage((prev) => (n === 0 ? 0 : Math.min(prev, n - 1)));
  }, [templatePreviewRef]);

  React.useEffect(() => {
    const container = templatePreviewRef.current;
    if (!container) return;
    syncPageCount();
    const observer = new MutationObserver(() => syncPageCount());
    observer.observe(container, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [templatePreviewRef, docxFile, syncPageCount]);

  React.useEffect(() => {
    const container = templatePreviewRef.current;
    if (!container) return;
    const sections = Array.from(container.querySelectorAll("section.docx")) as HTMLElement[];
    sections.forEach((el, i) => {
      el.style.display = i === activePage ? "block" : "none";
      el.style.transform = `scale(${zoom})`;
      el.style.transformOrigin = "top center";
      el.style.transition = "transform 0.15s ease-out";
    });
  }, [activePage, zoom, pageCount, templatePreviewRef, busy]);

  const handleReload = async () => {
    const ok = window.confirm(t("editor.reloadConfirm"));
    if (!ok) return;
    await onRenderTemplatePreview();
    syncPageCount();
    toastInfo(t("editor.reloaded"));
  };

  const handleDuplicate = async (pageIndex: number) => {
      if (!onDuplicateSection) return;
      const r = await onDuplicateSection(pageIndex);
      if (r === false) return;
      // Prefer engine-returned copy index; fall back to pageIndex+1.
      const next = typeof r === "number" ? r : Math.min(pageIndex + 1, pageCount);
      setActivePage(next);
    };

    const handleDelete = async (pageIndex: number) => {
      if (!onDeleteSection) return;
      const r = await onDeleteSection(pageIndex);
      if (r === false) return;
      const next =
        typeof r === "number" ? r : Math.max(0, Math.min(pageIndex, pageCount - 2));
      setActivePage(next);
    };

  return (
    <div className="space-y-3">
      {/* ── Sticky action bar ───────────────────────────────── */}
      <div className="sticky top-0 z-20 -mx-1 rounded-xl border bg-background/95 px-3 py-2 shadow-sm backdrop-blur supports-[backdrop-filter]:bg-background/80">
        <div className="flex flex-wrap items-center gap-2">
          {/* Left: close + title + info badges */}
          {onBackToUpload && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onBackToUpload}
              disabled={busy}
              className="h-8 gap-1.5 px-2 text-xs text-muted-foreground"
            >
              <X className="h-3.5 w-3.5" /> {backLabel ?? t("editor.close")}
            </Button>
          )}

          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="text-sm font-semibold tracking-tight">Chỉnh sửa template</span>
              {docxFile?.fileName && (
                <span className="max-w-[160px] truncate text-[11px] text-muted-foreground" title={docxFile.fileName}>
                  {docxFile.fileName}
                </span>
              )}
              {pageCount > 0 && (
                <Badge variant="secondary" className="h-5 px-1.5 text-[10px] font-normal">
                  {activePage + 1}/{pageCount}
                </Badge>
              )}
              <Badge variant="outline" className="h-5 px-1.5 text-[10px] font-normal">
                {docxFile?.fields.length ?? 0} placeholder
              </Badge>
              <Badge variant="outline" className="h-5 gap-0.5 px-1.5 text-[10px] font-normal">
                {matchedCount}/{headers.length} cột
              </Badge>
            </div>
          </div>

          {/* Right: zoom + edit actions + primary CTA */}
          {/* Zoom */}
          <div className="flex items-center gap-0.5 rounded-lg border bg-muted/30 p-0.5">
            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={handleZoomOut} title={t("editor.zoomOut")} disabled={busy}>
              <ZoomOut className="h-3.5 w-3.5" />
            </Button>
            <button
              type="button"
              onClick={handleResetZoom}
              className="min-w-[2.5rem] px-1 font-mono text-[11px] text-muted-foreground hover:text-foreground"
              title={t("editor.resetZoom")}
            >
              {Math.round(zoom * 100)}%
            </button>
            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={handleZoomIn} title={t("editor.zoomIn")} disabled={busy}>
              <ZoomIn className="h-3.5 w-3.5" />
            </Button>
          </div>

          {/* Edit toggle */}
          <Button
            size="sm"
            variant={templateEditMode ? "default" : "outline"}
            onClick={onToggleTemplateEdit}
            disabled={busy}
            className="h-8 gap-1.5 text-xs"
          >
            <Pencil className="h-3.5 w-3.5" />
            {templateEditMode ? t("editor.editOff") : t("editor.editOn")}
          </Button>

          {/* Reload — only when NOT editing */}
          {!templateEditMode && (
            <Button
              size="icon"
              variant="ghost"
              onClick={handleReload}
              disabled={busy}
              className="h-8 w-8"
              title={t("editor.reloadTemplate")}
              aria-label={t("editor.reloadTemplate")}
            >
              <RotateCcw className="h-3.5 w-3.5" />
            </Button>
          )}

          {/* Save — only visible while editing */}
          {templateEditMode && (
            <Button
              size="sm"
              variant="secondary"
              onClick={onSaveTemplateEdits}
              disabled={busy}
              className="h-8 gap-1.5 text-xs"
              title={t("editor.commitEdits")}
            >
              <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" /> Lưu
            </Button>
          )}

          {/* Download .docx — icon only */}
          {onDownloadTemplate && (
            <Button
              size="icon"
              variant="outline"
              onClick={onDownloadTemplate}
              disabled={busy}
              className="h-8 w-8"
              title={t("editor.downloadDocx")}
            >
              <Download className="h-3.5 w-3.5" />
            </Button>
          )}

          {/* Primary CTA */}
          <Button
            size="sm"
            onClick={onProceedToPreview}
            disabled={!canProceed || busy}
            className="h-8 gap-1.5 px-3 text-xs font-semibold shadow-sm"
          >
            {proceedLabel ?? t("editor.saveAndPreview")} <ArrowRight className="h-3.5 w-3.5" />
          </Button>

          {/* Toggle palette panel */}
          <Button
            size="icon"
            variant="ghost"
            onClick={() => setPaletteOpen((v) => !v)}
            className="h-8 w-8"
            title={paletteOpen ? t("editor.collapsePanel") : t("editor.openPanel")}
          >
            {paletteOpen ? <PanelRightClose className="h-4 w-4" /> : <PanelRightOpen className="h-4 w-4" />}
          </Button>
        </div>
      </div>

      {/* ── Main 2-column layout ────────────────────────────── */}
      <div className={cn("grid grid-cols-1 items-start gap-3 lg:grid-cols-4", !paletteOpen && "lg:grid-cols-1")}>
        {/* Canvas column */}
        <div className={cn("space-y-2", paletteOpen ? "lg:col-span-3" : "lg:col-span-1")}>
          <Card className="shadow-xs overflow-hidden">
            <CardContent className="space-y-2 p-3">
              {/* Page tabs — per-page actions live HERE only */}
              {pageCount > 0 ? (
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="mr-0.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                    Trang
                  </span>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 w-7 px-0"
                    disabled={activePage <= 0 || busy}
                    onClick={() => setActivePage((p) => Math.max(0, p - 1))}
                    title="Trang trước"
                  >
                    <ArrowLeft className="h-3.5 w-3.5" />
                  </Button>

                  {Array.from({ length: pageCount }, (_, i) => {
                    const isActive = i === activePage;
                    return (
                      <div
                        key={i}
                        className={cn(
                          "group flex items-center gap-0.5 rounded-lg border transition-colors",
                          isActive
                            ? "border-primary bg-primary/5"
                            : "border-transparent hover:border-muted"
                        )}
                      >
                        <Button
                          size="sm"
                          variant={isActive ? "default" : "ghost"}
                          className="h-7 px-2.5 text-xs"
                          onClick={() => setActivePage(i)}
                          disabled={busy}
                        >
                          {i + 1}
                        </Button>
                        {onDuplicateSection && (
                          <button
                            type="button"
                            className={cn(
                              "rounded p-1 text-muted-foreground transition-opacity hover:bg-primary/10 hover:text-primary",
                              isActive ? "opacity-100" : "opacity-0 group-hover:opacity-100"
                            )}
                            title="Nhân đôi trang này"
                            disabled={busy}
                            onClick={() => handleDuplicate(i)}
                          >
                            <Copy className="h-3 w-3" />
                          </button>
                        )}
                        {onDeleteSection && (
                          <button
                            type="button"
                            className={cn(
                              "rounded p-1 text-muted-foreground transition-opacity hover:bg-red-50 hover:text-red-600",
                              isActive ? "opacity-100" : "opacity-0 group-hover:opacity-100",
                              pageCount <= 1 && "pointer-events-none opacity-20"
                            )}
                            title={pageCount <= 1 ? "Không thể xóa trang duy nhất" : "Xóa trang này"}
                            disabled={busy || pageCount <= 1}
                            onClick={() => handleDelete(i)}
                          >
                            <Trash2 className="h-3 w-3" />
                          </button>
                        )}
                      </div>
                    );
                  })}

                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 w-7 px-0"
                    disabled={activePage >= pageCount - 1 || busy}
                    onClick={() => setActivePage((p) => Math.min(pageCount - 1, p + 1))}
                    title="Trang sau"
                  >
                    <ArrowRight className="h-3.5 w-3.5" />
                  </Button>

                  <span className="ml-auto hidden text-[11px] text-muted-foreground sm:inline">
                    Hover tab → Nhân đôi / Xóa · Bật Soạn Thảo để sửa nội dung
                  </span>
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">
                  Đang chờ canvas Word… bấm <strong>Tải Lại</strong> nếu trang không hiện.
                </p>
              )}

              {/* Format toolbar — only while editing */}
              {templateEditMode && (
                <div className="space-y-1.5 rounded-lg border border-amber-200/80 bg-amber-50/40 p-2 dark:border-amber-900/40 dark:bg-amber-950/20">
                  <TemplateFormatToolbar
                    containerRef={templatePreviewRef}
                    onChanged={recordEdit}
                    onAddTextOverlay={handleAddTextOverlay}
                    onAddImageOverlay={handleAddImageOverlay}
                    formats={formats}
                    onFormatsChange={onFormatsChange}
                    canUndo={undoStack.length >= 2}
                    canRedo={redoStack.length > 0}
                    onUndo={handleUndo}
                    onRedo={handleRedo}
                  />
                  <p className="px-0.5 text-[11px] text-amber-800 dark:text-amber-200">
                    Đang sửa <strong>Trang {activePage + 1}</strong>
                    {pageCount > 1 ? ` / ${pageCount}` : ""}. Click chữ để chỉnh · click ô bảng để thêm/xóa
                    hàng-cột · xong bấm <strong>Lưu File</strong> rồi <strong>{proceedLabel ?? t("editor.saveAndPreview")}</strong>.
                  </p>
                </div>
              )}

              {/* Overlay style panel — shown when an overlay is selected in edit mode */}
              {templateEditMode && selectedOverlay && (
                <div className="space-y-2 rounded-lg border border-blue-200/80 bg-blue-50/40 p-2 dark:border-blue-900/40 dark:bg-blue-950/20">
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] font-semibold text-blue-800 dark:text-blue-200">
                      {selectedOverlay.kind === "text" ? "Văn bản" : "Hình ảnh"} · đã chọn
                    </span>
                    <Button
                      size="sm"
                      variant="destructive"
                      className="h-6 px-2 text-[10px] gap-1"
                      onClick={handleDeleteOverlay}
                    >
                      <Trash2 className="h-3 w-3" /> Xóa
                    </Button>
                  </div>

                  {selectedOverlay.kind === "text" ? (
                    <div className="space-y-1.5">
                      <Input
                        value={selectedOverlay.text ?? ""}
                        onChange={(e) => handlePatchOverlay({ text: e.target.value })}
                        className="h-7 text-xs"
                        placeholder="Nhập nội dung văn bản"
                      />
                      <div className="flex flex-wrap items-center gap-1.5">
                        <label className="flex items-center gap-1 text-[10px] text-muted-foreground">
                          Cỡ chữ
                          <Input
                            type="number"
                            min={6}
                            max={72}
                            value={selectedOverlay.fontSize ?? 14}
                            onChange={(e) => handlePatchOverlay({ fontSize: Number(e.target.value) || 14 })}
                            className="h-6 w-14 text-xs"
                          />
                        </label>
                        <label className="flex items-center gap-1 text-[10px] text-muted-foreground">
                          Màu chữ
                          <input
                            type="color"
                            value={selectedOverlay.color ?? "#000000"}
                            onChange={(e) => handlePatchOverlay({ color: e.target.value })}
                            className="h-6 w-7 cursor-pointer rounded border p-0 bg-transparent"
                          />
                        </label>
                        <Select
                          value={selectedOverlay.fontWeight ?? "normal"}
                          onValueChange={(v) => handlePatchOverlay({ fontWeight: v })}
                        >
                          <SelectTrigger className="h-6 w-20 text-[10px]">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="normal">Thường</SelectItem>
                            <SelectItem value="bold">Đậm</SelectItem>
                          </SelectContent>
                        </Select>
                        <Select
                          value={selectedOverlay.textAlign ?? "left"}
                          onValueChange={(v) => handlePatchOverlay({ textAlign: v as "left" | "center" | "right" })}
                        >
                          <SelectTrigger className="h-6 w-20 text-[10px]">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="left">Trái</SelectItem>
                            <SelectItem value="center">Giữa</SelectItem>
                            <SelectItem value="right">Phải</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1.5">
                      <label className="flex items-center gap-1 text-[10px] text-muted-foreground">
                        Chiều rộng
                        <Input
                          type="number"
                          min={5}
                          max={100}
                          value={Math.round(selectedOverlay.width ?? 20)}
                          onChange={(e) => handlePatchOverlay({ width: Number(e.target.value) || 20 })}
                          className="h-6 w-14 text-xs"
                        />
                        %
                      </label>
                      <span className="text-[10px] text-muted-foreground">
                        Kéo góc để điều chỉnh kích thước
                      </span>
                    </div>
                  )}
                </div>
              )}

              {/* Word canvas */}
              <div
                className={cn("relative rounded-xl", dragOverCanvas && "ring-2 ring-primary/60")}
                onDragOver={(e) => {
                  if (templateEditMode) {
                    e.preventDefault();
                    e.dataTransfer.dropEffect = "copy";
                    if (!dragOverCanvas) setDragOverCanvas(true);
                  }
                }}
                onDragLeave={(e) => {
                  if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                    setDragOverCanvas(false);
                  }
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  setDragOverCanvas(false);
                  const text = e.dataTransfer.getData("text/plain");
                  const match = text.match(/^\{\{([^{}]+)\}\}$/);
                  const field = match ? match[1] : text;
                  if (field.trim()) insertField(field.trim());
                }}
              >
                <div
                  ref={templatePreviewRef}
                  className="min-h-[520px] max-h-[70vh] overflow-auto rounded-xl border bg-slate-100 p-4 shadow-inner dark:bg-slate-950"
                />

                {/* Autocomplete popup for {{… syntax */}
                {ac && acOptions.length > 0 && (
                  <div
                    className="absolute z-50 w-72 overflow-hidden rounded-lg border bg-background shadow-lg"
                    style={{ left: ac.x, top: ac.y }}
                  >
                    <p className="border-b bg-muted/30 px-2.5 py-1 text-[10px] text-muted-foreground">
                      Chèn trường dữ liệu · <kbd className="rounded bg-muted px-1">↑↓</kbd> chọn ·{" "}
                      <kbd className="rounded bg-muted px-1">Enter</kbd> chèn
                    </p>
                    <ul className="max-h-60 overflow-auto py-0.5">
                      {acOptions.map((h, idx) => {
                        const isMatched = templateFieldNames.has(h.trim().toLowerCase());
                        return (
                          <li key={h}>
                            <button
                              type="button"
                              className={cn(
                                "flex w-full items-center gap-1.5 px-2.5 py-1.5 text-left text-xs hover:bg-muted",
                                idx === acIndexRef.current && "bg-primary/10"
                              )}
                              onMouseDown={(e) => {
                                e.preventDefault();
                                insertAcField(h);
                              }}
                              onMouseEnter={() => {
                                acIndexRef.current = idx;
                              }}
                            >
                              {isMatched ? (
                                <Check className="h-3 w-3 shrink-0 text-emerald-600" />
                              ) : (
                                <span className="h-3 w-3 shrink-0 rounded-full border border-dashed border-muted-foreground/40" />
                              )}
                              <span className="truncate font-mono">{h}</span>
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Field palette — collapsible */}
        {paletteOpen && (
        <div className="space-y-2 lg:col-span-1">
          {/* Condition bar — above Cột Excel, top-right */}
          {conditionBar && conditionBar}

          {/* Carton split config — per-page */}
          {onSplitFieldChange && (
              <Card className="shadow-xs overflow-hidden">
                <CardHeader className="space-y-1 border-b bg-muted/20 px-3 py-2.5">
                  <CardTitle className="flex items-center gap-1.5 text-xs font-semibold">
                    <button
                      type="button"
                      className="flex items-center gap-1.5"
                      onClick={() => setSplitOpen((v) => !v)}
                    >
                      <ChevronRight className={cn("h-3.5 w-3.5 text-muted-foreground transition-transform", splitOpen && "rotate-90")} />
                      <Scissors className="h-3.5 w-3.5 text-primary" />
                      Tách trang
                    </button>
                  </CardTitle>
                  {splitOpen && (
                  <CardDescription className="text-[11px] leading-snug">
                    Chọn cột số — mỗi bản ghi tách thành N trang, đánh số 1/N…N/N.
                  </CardDescription>
                  )}
                </CardHeader>
                {splitOpen && (
                <CardContent className="space-y-1.5 p-2.5">
                  <Select
                    value={splitField || SPLIT_BY_ROWS}
                    onValueChange={(v) => onSplitFieldChange(v === SPLIT_BY_ROWS ? "" : v)}
                  >
                    <SelectTrigger className="h-8 w-full text-xs">
                      <SelectValue placeholder="Theo số dòng" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={SPLIT_BY_ROWS}>Theo số dòng trong bảng</SelectItem>
                      {(numericColumns && numericColumns.length > 0 ? numericColumns : headers).map((h) => (
                        <SelectItem key={h} value={h}>{h}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {splitField ? (
                    <div className="space-y-1">
                      <div className="flex items-center justify-between gap-1.5">
                        <Badge variant="secondary" className="h-5 text-[10px] font-normal">
                          1/{toTemplateVar(splitField)}
                        </Badge>
                        <code className="truncate rounded bg-muted px-1.5 py-0.5 font-mono text-[10px] text-primary">
                          {templateVarExpr(splitField)}
                        </code>
                      </div>
                    </div>
                  ) : (
                    <p className="text-[10px] text-muted-foreground">
                      Tách theo số dòng trong bảng chi tiết
                    </p>
                  )}
                  <div className="rounded-md border border-border/70 bg-muted/20 p-1.5 text-[10px] text-muted-foreground">
                    Biến kiểu Word trong template:
                    <div className="mt-1 flex flex-wrap items-center gap-1">
                      <code className="rounded bg-background px-1 py-0.5 font-mono text-[10px] text-primary">
                        {"{{PAGE}}"}
                      </code>
                      <span>số trang hiện tại</span>
                      <span className="text-border">·</span>
                      <code className="rounded bg-background px-1 py-0.5 font-mono text-[10px] text-primary">
                        {"{{NUMPAGES}}"}
                      </code>
                      <span>tổng số trang</span>
                    </div>
                  </div>
                </CardContent>
                )}
              </Card>
          )}

          <Card className="sticky top-16 border-primary/15 shadow-xs">
            <CardHeader className="space-y-1 border-b bg-muted/20 px-3 py-2.5">
              <CardTitle className="flex items-center justify-between text-xs font-semibold">
                <button
                  type="button"
                  className="flex items-center gap-1.5"
                  onClick={() => setExcelColOpen((v) => !v)}
                >
                  <ChevronRight className={cn("h-3.5 w-3.5 text-muted-foreground transition-transform", excelColOpen && "rotate-90")} />
                  <span>Cột Dữ Liệu</span>
                </button>
                <Badge variant="outline" className="h-5 max-w-[7rem] truncate bg-background text-[10px] font-normal" title={primarySheetName}>
                  {primarySheetName || "—"}
                </Badge>
              </CardTitle>
              {excelColOpen && (
              <CardDescription className="text-[11px] leading-snug">
                Kéo thả hoặc chép <code className="rounded bg-muted px-1">{`{{tên}}`}</code> vào canvas.
              </CardDescription>
              )}
            </CardHeader>
            {excelColOpen && (
            <CardContent className="space-y-2.5 p-3">
              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
                <Input
                  placeholder="Tìm cột…"
                  value={fieldSearch}
                  onChange={(e) => setFieldSearch(e.target.value)}
                  className="h-8 pl-8 text-xs"
                />
              </div>

              {secondarySheetName && (
                <p className="rounded-md border bg-muted/40 px-2 py-1.5 text-[11px] text-muted-foreground">
                  + Sheet phụ <strong className="text-foreground">{secondarySheetName}</strong>
                  {joinedAddedColumns.length > 0 ? ` · ${joinedAddedColumns.length} cột` : ""}
                </p>
              )}

              <div className="max-h-[56vh] space-y-1 overflow-y-auto pr-0.5">
                {filteredHeaders.length === 0 ? (
                  <p className="py-6 text-center text-xs italic text-muted-foreground">
                    Không có cột khớp.
                  </p>
                ) : (
                  filteredHeaders.map((header) => {
                    const isMatchedInTemplate = templateFieldNames.has(header.trim().toLowerCase());
                    const isSecondary = joinedAddedColumns.includes(header);
                    return (
                      <div
                        key={header}
                        draggable
                        onDragStart={(e) => {
                          e.dataTransfer.setData("text/plain", templateVarExpr(header) || `{{${header}}}`);
                        }}
                        className={cn(
                          "group flex items-center gap-1.5 rounded-lg border p-1.5 text-xs transition-all",
                          "cursor-grab bg-card active:cursor-grabbing hover:border-primary/40 hover:shadow-xs",
                          isMatchedInTemplate && "border-emerald-200/80 bg-emerald-50/40 dark:bg-emerald-950/15"
                        )}
                        title="Kéo thả vào canvas hoặc bấm Chèn"
                      >
                        {isMatchedInTemplate ? (
                          <Check className="h-3.5 w-3.5 shrink-0 text-emerald-600" />
                        ) : (
                          <span className="h-3.5 w-3.5 shrink-0 rounded-full border border-dashed border-muted-foreground/40" />
                        )}
                        <span className="min-w-0 flex-1">
                          <span className="block truncate font-mono text-[11px] font-semibold">{header}</span>
                          <code className="block truncate text-[9px] text-muted-foreground">
                            {templateVarExpr(header) || `{{${header}}}`}
                          </code>
                        </span>
                        {isSecondary && (
                          <Badge variant="outline" className="h-3.5 px-1 text-[9px]">
                            phụ
                          </Badge>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleCopyField(header)}
                          className="h-7 shrink-0 gap-1 px-1.5 text-[11px] opacity-80 hover:bg-primary/10 group-hover:opacity-100"
                          title={`Chép ${templateVarExpr(header) || `{{${header}}}`}`}
                        >
                          {copiedField === header ? (
                            <Check className="h-3 w-3 text-emerald-600" />
                          ) : (
                            <Copy className="h-3 w-3" />
                          )}
                        </Button>
                      </div>
                    );
                  })
                )}
              </div>
            </CardContent>
            )}
          </Card>
        </div>
        )}
      </div>
    </div>
  );
}
